/**
 * 공용 마크다운 뷰어 — 챗봇과 동일한 스타일을 모든 분석 결과 페이지에서 재사용.
 * 문단 간 리듬 · 헤더 크기 균형 · 리스트 마커 톤 조정.
 */
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '../../lib/utils'

interface MarkdownViewProps {
  children: string
  className?: string
  size?: 'sm' | 'md'
  variant?: 'dark' | 'light'   // dark = 대시보드 기본, light = 시정지시서 등 흰 배경
}

export default function MarkdownView({ children, className, size = 'md', variant = 'dark' }: MarkdownViewProps) {
  const base = size === 'sm' ? 'text-[13px]' : 'text-[14px]'
  const isLight = variant === 'light'

  // 색상 팔레트 — 테마별 분리
  const colors = isLight
    ? {
        root: 'text-zinc-800',
        h1: 'text-zinc-900 border-zinc-200',
        h2: 'text-zinc-900',
        h3: 'text-amber-700',
        h4: 'text-zinc-800',
        p: 'text-zinc-800',
        li: 'text-zinc-800',
        strong: 'text-zinc-900',
        em: 'text-zinc-600',
        marker: 'marker:text-zinc-400',
        link: 'text-amber-600 hover:text-amber-500',
        code: 'bg-zinc-100 text-zinc-800',
        hr: 'border-zinc-200',
        bq: 'border-zinc-300 text-zinc-600',
        tableBorder: 'border-zinc-200',
        tHead: 'bg-zinc-50',
        th: 'text-zinc-900 border-zinc-200',
        td: 'text-zinc-800 border-zinc-100',
      }
    : {
        root: 'text-ink',
        h1: 'text-ink border-surface-elevated/30',
        h2: 'text-ink',
        h3: 'text-brand-primary-light',
        h4: 'text-ink',
        p: 'text-ink',
        li: 'text-ink',
        strong: 'text-ink font-bold',
        em: 'text-ink-muted',
        marker: 'marker:text-ink-faint',
        link: 'text-brand-primary-light hover:text-brand-primary',
        code: 'bg-surface-elevated/30 text-brand-secondary-light',
        hr: 'border-surface-elevated/30',
        bq: 'border-brand-primary/40 text-ink-muted',
        tableBorder: 'border-surface-elevated/40',
        tHead: 'bg-surface-elevated/20',
        th: 'text-ink border-surface-elevated/40',
        td: 'text-ink border-surface-elevated/20',
      }

  return (
    <div className={cn(base, 'leading-[1.75] space-y-2', colors.root, className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h2 className={cn('text-[18px] font-bold mt-5 mb-3 pb-2 border-b', colors.h1)}>{children}</h2>
          ),
          h2: ({ children }) => (
            <h3 className={cn('text-[16px] font-bold mt-4 mb-2', colors.h2)}>{children}</h3>
          ),
          h3: ({ children }) => (
            <h4 className={cn('text-[14.5px] font-semibold mt-3 mb-1.5', colors.h3)}>{children}</h4>
          ),
          h4: ({ children }) => (
            <h5 className={cn('text-[13.5px] font-semibold mt-2 mb-1', colors.h4)}>{children}</h5>
          ),
          p: ({ children }) => <p className={cn('text-[14px] leading-[1.75] my-2', colors.p)}>{children}</p>,
          ul: ({ children }) => (
            <ul className={cn('list-disc pl-5 my-2 space-y-1', colors.marker)}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className={cn('list-decimal pl-5 my-2 space-y-1', colors.marker)}>{children}</ol>
          ),
          li: ({ children }) => (
            <li className={cn('text-[14px] leading-[1.7]', colors.li)}>{children}</li>
          ),
          strong: ({ children }) => <strong className={cn('font-semibold', colors.strong)}>{children}</strong>,
          em: ({ children }) => <em className={cn('italic', colors.em)}>{children}</em>,
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn('underline decoration-dotted underline-offset-2', colors.link)}
            >
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code className={cn('px-1.5 py-0.5 rounded text-[13px] font-mono', colors.code)}>
              {children}
            </code>
          ),
          hr: () => <hr className={cn('my-4', colors.hr)} />,
          blockquote: ({ children }) => (
            <blockquote className={cn('border-l-2 pl-3 my-2', colors.bq)}>
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className={cn('overflow-x-auto my-3 rounded-lg border', colors.tableBorder)}>
              <table className="w-full text-[13px] border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className={colors.tHead}>{children}</thead>,
          th: ({ children }) => (
            <th className={cn('px-3 py-2 text-left font-semibold border-b', colors.th)}>
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className={cn('px-3 py-2 border-b', colors.td)}>{children}</td>
          ),
          img: ({ src, alt }) => (
            <img src={src} alt={alt ?? ''} className="rounded-lg my-3 max-w-full" />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}
